/* global  MbpmClient: false, MbClient: false */
/* eslint-disable max-len */
import api from 'app/shared/utils/api';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';
import DispatchConstants from 'app/shared/constants/DispatchConstants';
import EnvSelectors from 'app/shared/selectors/EnvSelectors';
import ListingActivationUtils from 'app/shared/utils/ListingActivationUtils';

// service
import appLogger from 'app/shared/loggers/appLogger';

// utils
import scriptUtils from 'app/shared/utils/scriptUtils';

const MAX_RETRY = 10;
const PERFORM_CHARGE_MAX_RETRY = 5;
// const DELAY_MS = 1500; // delay to work around the flickering when rendering iframe

const PaidInclusionActions = {
    loadCreditCardIframe(apiAccessToken, listing) {
        return (dispatch, getState) => {
            const configUrlCapture = EnvSelectors.makeMetroidConfigUrlCapture(getState());
            const handleSubmitSuccess = (token) => {
                dispatch({
                    type: DispatchConstants.CREDIT_CARD_FORM_SUBMIT_SUCCESS,
                });
                dispatch(PaidInclusionActions.verifyCreditCardCapture(token, listing));
            };
            const handleSubmitFail = (error) => {
                appLogger.logError({
                    message: `Could not load credit card iframe. Error: ${JSON.stringify(error)}.`,
                    context: 'PaidInclusionActions.loadCreditCardIframe',
                    user: getState().user,
                    listingId: listing.listingId,
                });

                dispatch({
                    type: DispatchConstants.CREDIT_CARD_FORM_SUBMIT_FAIL,
                    payload: error,
                });
                if (error && error.code === 'ZE-UI') {
                    dispatch(PaidInclusionActions.loadCreditCardIframe(apiAccessToken, listing));
                }
            };
            const handleLoadComplete = () => dispatch({
                type: DispatchConstants.CREDIT_CARD_FORM_LOAD_COMPLETE,
            });
            const handleCaptureCallback = () => dispatch({
                type: DispatchConstants.CREDIT_CARD_FORM_SUBMITTING,
            });
            try {
                MbpmClient.renderCreditCardIframe(
                    PaidInclusionConstants.CREDIT_CARD_FORM.ID_SELECTOR,
                    PaidInclusionConstants.CREDIT_CARD_FORM.CLASS_SELECTOR,
                    apiAccessToken,
                    configUrlCapture,
                    false,
                    handleSubmitSuccess,
                    handleSubmitFail,
                    handleLoadComplete,
                    PaidInclusionConstants.CREDIT_CARD_FORM.VERSION,
                    null,
                    null,
                    handleCaptureCallback,
                );
            } catch (error) {
                appLogger.logError({
                    message: `Could not render credit card iframe. Error: ${JSON.stringify(error)}.`,
                    context: 'PaidInclusionActions.loadCreditCardIframe.MbpmClient.renderCreditCardIframe',
                    user: getState().user,
                    listingId: listing.listingId,
                });
            }
        };
    },

    cleanupCreditCardIframe(listing, user) {
        return () => {
            try {
                MbpmClient.cleanUpPaymentMethodIframe(PaidInclusionConstants.CREDIT_CARD_FORM.ID_SELECTOR);
            } catch (error) {
                appLogger.logError({
                    message: `Could not render credit card iframe. Error: ${JSON.stringify(error)}.`,
                    context: 'PaidInclusionActions.cleanupCreditCardIframe.MbpmClient.cleanUpPaymentMethodIframe',
                    user,
                    listingId: listing.listingId,
                });
            }
        };
    },

    performCharge(apiAccessToken, listing, user, retryCount = 0) {
        const handlePerformChargeError = (dispatch, error, context) => {
            dispatch({
                type: DispatchConstants.PERFORM_CHARGE_FAIL,
                payload: error,
            });

            appLogger.logError({
                message: `Could not perform charge. Error: ${error.message}. Retry count: ${retryCount}`,
                context,
                user,
                listingId: listing.listingId,
            });

            // retry charge if the error.message is falsy (did not come from metroid)
            if (!error.message && retryCount < PERFORM_CHARGE_MAX_RETRY) {
                dispatch(PaidInclusionActions.performCharge(apiAccessToken, listing, user, retryCount + 1));
            }
        };

        /*
         * The metroid client (MbClient) is defined a script via SSR
         * Sometimes the is not defined when we try and preform a charge
         * Check to see if the client is not defined.
         * If not, load the script that defines MbClient
         *
         * return MbClient
         */
        const getOrFetchMbClient = async (dispatch, getState) => {
            // check if client is not defined
            if (typeof MbClient === 'undefined') {
                // define client via script
                const env = __DEV__ ? 'DEV' : 'PROD';
                const scriptSrc = PaidInclusionConstants.CHARGE[env].SCRIPT_APP;

                await scriptUtils.loadScript(scriptSrc, 'pi-charge-app');

                return {
                    MbClient,
                    dispatch,
                    getState,
                };
            }

            // return client
            return {
                MbClient,
                dispatch,
                getState,
            };
        };

        const performChargeWithClient = ({ dispatch, getState, MbClient }) => {
            const configUrlCharge = EnvSelectors.makeMetroidConfigUrlCharge(getState());

            MbClient.performCharge(apiAccessToken, configUrlCharge)
                .then((response) => {
                    dispatch({
                        type: DispatchConstants.PERFORM_CHARGE_SUCCESS,
                        payload: response,
                    });

                    appLogger.logError({
                        type: 'INFO',
                        message: `MbClient.performCharge success. RetryCount: ${retryCount}`,
                        context: 'PaidInclusionActions.performCharge',
                        user,
                        listingId: listing.listingId,
                    });
                    return { success: true };
                })
                .catch((error) => {
                    const context = 'PaidInclusionActions.performCharge.MbClient.performCharge';
                    handlePerformChargeError(dispatch, error, context);
                    return { success: false };
                });
        };

        return (dispatch, getState) => {
            dispatch({
                type: DispatchConstants.PERFORM_CHARGE,
            });

            try {
                appLogger.logError({
                    type: 'INFO',
                    message: 'before MbClient.performCharge',
                    context: 'PaidInclusionActions.performCharge',
                    user,
                    listingId: listing.listingId,
                });

                // get metroid client and do the charge
                return getOrFetchMbClient(dispatch, getState).then(performChargeWithClient);
            } catch (error) {
                const context = 'PaidInclusionActions.performCharge';
                handlePerformChargeError(dispatch, error, context);
            }
            return Promise.resolve({ success: false });
        };
    },

    initiateCreditCardCapture(listing, retryCount = 0) {
        return (dispatch, getState) => {
            const metroidProductId = EnvSelectors.makeMetroidProductId(getState());
            return dispatch(api.users.properties.paidInclusion.initiateCreditCardCapture(listing.listingId,
                metroidProductId))
                .then((response) => {
                    if (response.success) {
                        dispatch({
                            type: DispatchConstants.INITIATE_CREDIT_CARD_CAPTURE_SUCCESS,
                            payload: response.response,
                        });
                        return response;
                    }
                    if (ListingActivationUtils.isFailedActivationErrorCode(response.error.code)) {
                        dispatch({
                            type: DispatchConstants.LISTING_CANNOT_BE_ACTIVATED,
                            payload: response.error,
                        });
                        return response;
                    }
                    switch (response.error.code) {
                        case PaidInclusionConstants.CAPTURE.INITIATE.ERROR.METROID_MAINTENANCE_MODE:
                            dispatch({
                                type: DispatchConstants.INITIATE_CREDIT_CARD_CAPTURE_FAIL,
                                payload: response.error,
                            });
                            break;
                        default:
                            if (retryCount < MAX_RETRY) {
                                dispatch(PaidInclusionActions.initiateCreditCardCapture(listing, retryCount + 1));
                            } else {
                                dispatch({
                                    type: DispatchConstants.INITIATE_CREDIT_CARD_CAPTURE_FAIL,
                                    payload: response.error,
                                });
                            }
                            break;
                    }
                    return response;
                });
        };
    },

    verifyCreditCardCapture(apiAccessToken) {
        return dispatch => dispatch(api.users.properties.paidInclusion.verifyCreditCardCapture(apiAccessToken))
            .then((response) => {
                if (response.success) {
                    dispatch({
                        type: DispatchConstants.VERIFY_CREDIT_CARD_CAPTURE_SUCCESS,
                        payload: response.response,
                    });
                } else {
                    dispatch({
                        type: DispatchConstants.VERIFY_CREDIT_CARD_CAPTURE_FAIL,
                        payload: response.error,
                    });
                }
                return response;
            });
    },

    initiateCreditCardCharge(listing, retryCount = 0) {
        return (dispatch, getState) => {
            const metroidProductId = EnvSelectors.makeMetroidProductId(getState());
            return dispatch(api.users.properties.paidInclusion.initiateCreditCardCharge(listing.listingId, metroidProductId))
                .then((response) => {
                    if (response.success) {
                        dispatch({
                            type: DispatchConstants.INITIATE_CREDIT_CARD_CHARGE_SUCCESS,
                            payload: response.response,
                        });
                    } else if (ListingActivationUtils.isFailedActivationErrorCode(response.error.code)) {
                        dispatch({
                            type: DispatchConstants.LISTING_CANNOT_BE_ACTIVATED,
                            payload: response.error,
                        });
                        return response;
                    } else if (response.error.code === PaidInclusionConstants.CHARGE.INITIATE.ERROR.TRY_AGAIN_LATER) {
                        dispatch({
                            type: DispatchConstants.INITIATE_CREDIT_CARD_CHARGE_PENDING,
                            payload: response.error,
                        });
                    } else if (response.error.code === PaidInclusionConstants.CHARGE.INITIATE.ERROR.METROID_MAINTENANCE_MODE) {
                        dispatch({
                            type: DispatchConstants.INITIATE_CREDIT_CARD_CHARGE_FAIL,
                            payload: response.error,
                        });
                    } else if (response.error.code === PaidInclusionConstants.CHARGE.INITIATE.ERROR.VALID_PAYMENT_EXISTS) {
                        dispatch({
                            type: DispatchConstants.INITIATE_CREDIT_CARD_CHARGE_VALID_PAYMENT_EXISTS,
                            payload: response.error,
                        });
                    } else if (retryCount < MAX_RETRY) {
                        dispatch(PaidInclusionActions.initiateCreditCardCharge(listing, retryCount + 1));
                    } else {
                        dispatch({
                            type: DispatchConstants.INITIATE_CREDIT_CARD_CHARGE_FAIL,
                            payload: response.error,
                        });
                    }
                    return response;
                });
        };
    },

    verifyPaymentAndActivateListing(listing, includePaymentMethod = false, isPostCharge = false, hostApp) {
        return dispatch => dispatch(api.users.properties.paidInclusion.verifyPaymentAndActivateListing(listing.listingId, hostApp, includePaymentMethod))
            .then((response) => {
                if (response.success) {
                    dispatch({
                        type: DispatchConstants.VERIFY_PAYMENT_AND_ACTIVATE_LISTING_SUCCESS,
                        payload: {
                            ...response.response,
                            isPostCharge,
                        },
                    });
                } else {
                    dispatch({
                        type: DispatchConstants.VERIFY_PAYMENT_AND_ACTIVATE_LISTING_FAIL,
                        payload: {
                            ...response.error,
                            isPostCharge,
                        },
                    });
                }
                return response;
            });
    },

    getPrice() {
        return dispatch => dispatch(api.users.properties.paidInclusion.getPrice())
            .then((response) => {
                if (response.success) {
                    dispatch({
                        type: DispatchConstants.GET_PAID_INCLUSION_PRICE_SUCCESS,
                        payload: {
                            ...response.response,
                        },
                    });
                } else {
                    dispatch({
                        type: DispatchConstants.GET_PAID_INCLUSION_PRICE_FAIL,
                        payload: response.error,
                    });
                }
                return response;
            });
    },

    details(listingId) {
        return dispatch => dispatch(api.users.properties.paidInclusion.details(listingId))
            .then((response) => {
                if (response.success) {
                    dispatch({
                        type: DispatchConstants.PAID_INCLUSION_DETAILS_SUCCESS,
                        payload: {
                            ...response.response,
                        },
                    });
                } else {
                    dispatch({
                        type: DispatchConstants.PAID_INCLUSION_DETAILS_FAIL,
                        payload: response.error,
                    });
                }
                return response;
            });
    },

    getDefaultPaymentMethod(listing) {
        return dispatch => dispatch(api.users.properties.paidInclusion.defaultPaymentMethod(listing.listingId))
            .then((response) => {
                if (response.success) {
                    dispatch({
                        type: DispatchConstants.DEFAULT_PAYMENT_SUCCESS,
                        payload: {
                            ...response.response,
                        },
                    });
                } else {
                    dispatch({
                        type: DispatchConstants.DEFAULT_PAYMENT_FAIL,
                        payload: response.error,
                    });
                }
                return response;
            });
    },
};

export default PaidInclusionActions;
